import { useCallback, useEffect, useState } from "react";
import { CreditCard, Loader2, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Subscription prices live inside one Stripe account — the live account starts out empty. */
export const StripePricesCard = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stripe/prices");
      setState(data);
    } catch (e) { setError(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy(true); setError(""); setDone("");
    try {
      const { data } = await api.post("/admin/stripe/sync-prices");
      setDone(`${data.prices} prices across ${data.plans} plans are set up in your ${data.mode} Stripe account.`);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  if (!state) return null;
  const ready = state.missing.length === 0;

  return (
    <div data-testid="stripe-prices-card" className="rounded-xl border border-ink-600 bg-ink-800 p-6">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
        <CreditCard className="h-3.5 w-3.5" /> Subscription prices · {state.mode} account
      </p>
      <p className="mt-3 text-sm text-slate-300" data-testid="stripe-prices-status">
        {ready
          ? `All ${state.expected} plan prices are set up, so operators can subscribe.`
          : `${state.missing.length} of ${state.expected} plan prices are missing from this Stripe account — operators would hit an error at checkout.`}
      </p>
      {!ready && (
        <p className="mt-2 font-mono text-[11px] text-amber-300" data-testid="stripe-prices-missing">
          {state.missing.slice(0, 6).join(" · ")}
        </p>
      )}
      <p className="mt-3 text-xs leading-relaxed text-slate-500">
        Stripe keeps test and live separate. The day you claim your live account, press this once and
        every price is created there — weekly, 3 months, 6 months and yearly for each plan, plus the
        win-back coupon.
      </p>
      <Button data-testid="stripe-prices-sync" size="sm" variant="outline" className="mt-4"
        disabled={busy} onClick={sync}>
        {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
        {ready ? "Check and refresh prices" : "Set up the prices now"}
      </Button>
      {done && <p data-testid="stripe-prices-done" className="mt-3 text-xs text-emerald-400">{done}</p>}
      {error && <p data-testid="stripe-prices-error" className="mt-3 text-xs text-red-400">{error}</p>}
    </div>
  );
};
